import { useEffect, useState, useCallback, useRef } from 'react';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Users, Image as ImageIcon, X, Send, MessageCircle, Bookmark, Flag,
  MoreHorizontal, Trash2, Pencil, Pin, Lock, ShieldAlert, Loader2, AlertTriangle,
  Search, TrendingUp, Sparkles, Sprout, Flame, Heart, Smile, ThumbsUp,
  ChevronLeft, Star,
} from 'lucide-react';

// Lightweight emoji picker — a curated, friendly set (no heavy dependency).
const EMOJIS = ['👍','❤️','🙏','🔥','💪','👏','😊','😂','🥹','😢','🙌','✨','💯','🤝','🫶','😮','🎉','👊','🧠','🌱'];
function EmojiPicker({ onPick, onClose }) {
  return (
    <div className="absolute z-30 bottom-full mb-2 right-0 bg-white border border-navy/10 rounded-2xl p-2 shadow-xl grid grid-cols-5 gap-1 w-56" data-testid="emoji-picker" onMouseLeave={onClose}>
      {EMOJIS.map((e) => (
        <button key={e} type="button" data-testid={`emoji-${e}`} onClick={() => onPick(e)}
          className="text-xl w-9 h-9 rounded-lg hover:bg-beige transition-smooth">{e}</button>
      ))}
    </div>
  );
}

const BADGE_ICON = { sprout: Sprout, users: Users, flame: Flame, heart: Heart };

function AuthorBadges({ badges }) {
  if (!badges || !badges.length) return null;
  return (
    <span className="inline-flex items-center gap-1">
      {badges.slice(0, 2).map((b) => {
        const Icon = BADGE_ICON[b.icon] || Sprout;
        return (
          <span key={b.key} data-testid={`community-badge-${b.key}`} title={b.label}
            className="inline-flex items-center gap-0.5 text-[10px] font-medium text-gold bg-gold/10 rounded-full px-1.5 py-0.5">
            <Icon size={10}/> {b.label}
          </span>
        );
      })}
    </span>
  );
}

const REACTION_LABEL = {
  support: 'Support', respect: 'Respect', helpful: 'Helpful',
  relate: 'I relate', encouragement: 'Encouragement', celebrate: 'Celebrate',
};

const IDENTITY_OPTS = (community) => ([
  { value: 'display', label: 'Display name' },
  { value: 'first', label: 'First name only' },
  { value: 'anonymous', label: community === 'supporter' ? 'Anonymous Supporter' : 'Anonymous Brother' },
]);

const REPORT_REASONS = [
  'Harassment or bullying', 'Hate speech', 'Spam', 'Harmful advice',
  'Privacy violation', 'Sharing confidential information', 'Crisis or safety concern',
  'Threats or violence', 'Misinformation', 'Other',
];

function fmt(iso) {
  if (!iso) return '';
  const d = new Date(iso); if (isNaN(d)) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function resolveAvatarUrl(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
}

function Avatar({ author }) {
  if (author?.avatar) return <img src={resolveAvatarUrl(author.avatar)} alt="" className="w-9 h-9 rounded-full object-cover"/>;
  const anon = author?.identity === 'anonymous';
  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-heading ${anon ? 'bg-navy/10 text-navy/60' : 'bg-navy text-white'}`}>
      {anon ? '?' : (author?.name || 'M').slice(0, 1).toUpperCase()}
    </div>
  );
}

export default function Community({ community }) {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('latest'); // latest | trending | for_you
  const [savedOnly, setSavedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [cat, setCat] = useState('all');
  const [browsing, setBrowsing] = useState(true); // topic-grid landing vs feed view
  const [followingFeed, setFollowingFeed] = useState(false); // combined feed of followed topics
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [reportTarget, setReportTarget] = useState(null); // {type,id}

  const title = community === 'supporter' ? 'Supporter Community' : 'Brotherhood Feed';
  const subtitle = community === 'supporter'
    ? 'A private space for those supporting a man in their life.'
    : 'A private, supportive space for the brotherhood.';

  const loadMeta = useCallback(async () => {
    try { const r = await http.get(`/community/${community}/meta`); setMeta(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load community'); }
  }, [community]);

  const load = useCallback(async (reset = true, currentCount = 0) => {
    setLoading(true);
    try {
      const skip = reset ? 0 : currentCount;
      const params = { category: cat, sort, saved: savedOnly, skip, limit: 20 };
      if (followingFeed) { params.following = true; delete params.category; }
      if (debouncedSearch.trim().length >= 2) params.q = debouncedSearch.trim();
      const r = await http.get(`/community/${community}/feed`, { params });
      setItems((prev) => reset ? r.data.items : [...prev, ...r.data.items]);
      setTotal(r.data.total); setHasMore(r.data.has_more);
    } catch (e) { toast.error('Could not load the feed'); }
    finally { setLoading(false); }
  }, [community, cat, sort, savedOnly, debouncedSearch, followingFeed]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { load(true); }, [load]);

  const onCreated = (post) => setItems((prev) => [post, ...prev]);
  const onUpdated = (post) => setItems((prev) => prev.map((p) => p.id === post.id ? post : p));
  const onRemoved = (id) => { setItems((prev) => prev.filter((p) => p.id !== id)); setTotal((t) => Math.max(0, t - 1)); };

  const toggleFollow = async (category) => {
    try {
      const r = await http.post(`/community/${community}/topics/follow`, { category });
      setMeta((m) => ({
        ...m,
        followed_categories: r.data.followed_categories,
        category_meta: (m.category_meta || []).map((t) => t.key === category ? { ...t, following: r.data.following } : t),
      }));
      toast.success(r.data.following ? `Following ${category}` : `Unfollowed ${category}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update follow'); }
  };

  if (!meta) {
    return <div className="p-10 text-center text-navy/50"><Loader2 className="inline animate-spin" size={20}/></div>;
  }

  return (
    <div className="p-5 lg:px-10 lg:pt-8 pb-24 max-w-2xl mx-auto" data-testid="community-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-navy text-gold flex items-center justify-center shrink-0">
          <Users size={20} strokeWidth={1.75}/>
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl lg:text-3xl">{title}</h1>
          <p className="text-sm text-navy/55 mt-0.5">{subtitle}</p>
        </div>
      </div>

      {/* Crisis note */}
      <div className="mt-4 flex items-start gap-2 rounded-2xl bg-red-50 border border-red-200 px-4 py-2.5" data-testid="community-crisis">
        <ShieldAlert size={15} className="text-red-700 mt-0.5 shrink-0"/>
        <p className="text-xs text-navy/75 leading-relaxed">
          This community isn&rsquo;t monitored 24/7 and isn&rsquo;t an emergency service. In a crisis, call <b>911</b> or call/text <b>988</b>.
        </p>
      </div>

      {/* Composer */}
      <Composer
        community={community} meta={meta} user={user}
        onNeedGuidelines={() => setShowGuidelines(true)}
        onCreated={onCreated}
      />

      {browsing ? (
        /* Topic card grid — the community landing */
        <TopicGrid
          meta={meta}
          onSelect={(key) => { setFollowingFeed(false); setCat(key); setSavedOnly(false); setSearch(''); setBrowsing(false); }}
          onToggleFollow={toggleFollow}
          onOpenFollowing={() => { setFollowingFeed(true); setCat('all'); setSavedOnly(false); setSearch(''); setBrowsing(false); }}
        />
      ) : (
        <>
          {/* Back to topics */}
          <button onClick={() => { setBrowsing(true); setFollowingFeed(false); setCat('all'); setSavedOnly(false); setSearch(''); }}
            data-testid="community-back-to-topics"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-navy/70 hover:text-navy transition-smooth">
            <ChevronLeft size={16}/> Back to topics
          </button>

          {/* Active topic heading */}
          {followingFeed ? (
            <h2 className="mt-2 font-heading text-xl text-navy inline-flex items-center gap-2" data-testid="community-active-topic">
              <Star size={18} className="text-gold" fill="currentColor"/> Following
            </h2>
          ) : cat !== 'all' && (
            <h2 className="mt-2 font-heading text-xl text-navy" data-testid="community-active-topic">{cat}</h2>
          )}

          {/* Search */}
          <div className="mt-4 relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-navy/40"/>
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="community-search"
              placeholder="Search posts…"
              className="w-full bg-white rounded-full pl-10 pr-9 py-2.5 text-sm ring-1 ring-navy/10 focus:outline-none focus:ring-2 focus:ring-gold"/>
            {search && (
              <button onClick={() => setSearch('')} data-testid="community-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy"><X size={15}/></button>
            )}
          </div>

          {/* Sort tabs + Saved toggle */}
          <div className="mt-3 flex items-center gap-2">
            {[
              { key: 'latest', label: 'Latest', Icon: Sparkles },
              { key: 'trending', label: 'Trending', Icon: TrendingUp },
              { key: 'for_you', label: 'For You', Icon: Heart },
            ].map(({ key, label, Icon }) => (
              <button key={key} onClick={() => { setSort(key); setSavedOnly(false); }} data-testid={`community-sort-${key}`}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-smooth ${sort === key && !savedOnly ? 'bg-navy text-white' : 'bg-white text-navy/70 ring-1 ring-navy/10 hover:ring-navy/30'}`}>
                <Icon size={13}/> {label}
              </button>
            ))}
            <button onClick={() => setSavedOnly((s) => !s)} data-testid="community-tab-saved"
              className={`ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-smooth ${savedOnly ? 'bg-gold text-navy' : 'bg-white text-navy/70 ring-1 ring-navy/10 hover:ring-navy/30'}`}>
              <Bookmark size={13} fill={savedOnly ? 'currentColor' : 'none'}/> Saved
            </button>
          </div>

          {/* Feed */}
          <div className="mt-4 space-y-3">
            {loading && items.length === 0 && (
              <div className="bg-white rounded-2xl p-8 text-center text-navy/45 card-soft"><Loader2 className="inline animate-spin" size={18}/></div>
            )}
            {!loading && items.length === 0 && (
              <div className="bg-white rounded-2xl p-10 text-center card-soft" data-testid="community-empty">
                <MessageCircle className="mx-auto text-navy/25" size={28}/>
                <p className="font-heading text-lg mt-3">{debouncedSearch.trim().length >= 2 ? 'No posts match your search.' : savedOnly ? 'No saved posts yet.' : followingFeed ? 'Nothing from your topics yet.' : 'Be the first to post.'}</p>
                <p className="text-sm text-navy/50 mt-1">{debouncedSearch.trim().length >= 2 ? 'Try a different word or clear the search.' : savedOnly ? 'Tap the bookmark on any post to save it here.' : followingFeed ? 'Follow a few more topics or check back soon.' : 'Share a win, ask a question, or start a conversation.'}</p>
              </div>
            )}
            {items.map((p) => (
              <PostCard key={p.id} post={p} community={community} meta={meta}
                onUpdated={onUpdated} onRemoved={onRemoved} onReport={(t) => setReportTarget(t)}/>
            ))}
            {hasMore && (
              <button onClick={() => load(false, items.length)} data-testid="community-load-more"
                className="w-full py-3 rounded-2xl bg-white ring-1 ring-navy/10 text-sm text-navy/70 hover:text-navy transition-smooth">
                {loading ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        </>
      )}

      {showGuidelines && (
        <GuidelinesModal community={community} meta={meta}
          onClose={() => setShowGuidelines(false)}
          onAccepted={() => { setMeta({ ...meta, guidelines_accepted: true }); setShowGuidelines(false); toast.success('Thanks — you can post now.'); }}/>
      )}
      {reportTarget && (
        <ReportModal community={community} target={reportTarget} onClose={() => setReportTarget(null)}/>
      )}
    </div>
  );
}

function TopicGrid({ meta, onSelect, onToggleFollow, onOpenFollowing }) {
  const topics = meta.category_meta || (meta.categories || []).map((c) => ({ key: c, title: c, icon: 'book', accent: '#C9A96E', description: '', post_count: 0, following: false }));
  const total = typeof meta.total_posts === 'number'
    ? meta.total_posts
    : topics.reduce((a, t) => a + (t.post_count || 0), 0);
  const followedCount = (meta.followed_categories || []).length;
  const followingPosts = meta.following_post_count || 0;

  const Card = ({ testid, accent, title, description, count, following, showFollow, image }) => (
    <div data-testid={testid} onClick={() => onSelect(title === 'All Posts' ? 'all' : title)}
      className={`group relative cursor-pointer overflow-hidden bg-white rounded-2xl card-soft border transition-smooth flex flex-col hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(27,46,75,0.10)] ${following ? 'border-gold/60 ring-1 ring-gold/40' : 'border-navy/5'}`}>
      <div className="relative w-full aspect-[16/10] overflow-hidden" style={image ? undefined : { backgroundColor: accent }}>
        {image && <img src={image} alt="" loading="lazy" className="w-full h-full object-cover transition-smooth group-hover:scale-[1.04]"/>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent"/>
        {showFollow && (
          <button type="button" data-testid={`community-follow-${title}`} title={following ? 'Unfollow topic' : 'Follow topic'}
            onClick={(e) => { e.stopPropagation(); onToggleFollow(title); }}
            className={`absolute top-2 right-2 p-1.5 rounded-full bg-white/85 backdrop-blur-sm shadow-sm transition-smooth ${following ? 'text-gold' : 'text-navy/40 hover:text-gold'}`}>
            <Star size={15} fill={following ? 'currentColor' : 'none'}/>
          </button>
        )}
      </div>
      <div className="p-3.5 flex flex-col gap-1 flex-1">
        <h3 className="font-heading text-base text-navy leading-tight">{title}</h3>
        {description && <p className="text-xs text-navy/55 leading-snug">{description}</p>}
        <p className="mt-auto pt-1.5 text-[11px] text-navy/40 inline-flex items-center gap-1">
          <MessageCircle size={11}/> {count} post{count === 1 ? '' : 's'}
        </p>
      </div>
    </div>
  );

  return (
    <div className="mt-5" data-testid="community-topics-grid">
      {/* Following — highlighted row above the grid */}
      {followedCount > 0 && (
        <button type="button" data-testid="community-following-open" onClick={onOpenFollowing}
          className="w-full mb-4 flex items-center gap-3 rounded-2xl p-4 text-left border border-gold/40 bg-gradient-to-r from-gold/15 to-gold/5 hover:shadow-md transition-smooth">
          <div className="w-11 h-11 rounded-xl bg-gold/25 text-gold flex items-center justify-center shrink-0">
            <Star size={20} fill="currentColor"/>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-base text-navy">Following</h3>
            <p className="text-xs text-navy/60">
              {followedCount} topic{followedCount === 1 ? '' : 's'} · {followingPosts} post{followingPosts === 1 ? '' : 's'}
            </p>
          </div>
          <ChevronLeft size={18} className="rotate-180 text-navy/40 shrink-0"/>
        </button>
      )}

      <p className="text-xs uppercase tracking-widest text-navy/45 font-medium mb-3">Topics</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card testid="community-topic-all" accent="#1B2E4B"
          title="All Posts" description="Everything from the community." count={total} showFollow={false}/>
        {topics.map((t) => (
          <Card key={t.key} testid={`community-topic-${t.key}`}
            accent={t.accent || '#C9A96E'} title={t.title} description={t.description} image={t.image}
            count={t.post_count || 0} following={!!t.following} showFollow/>
        ))}
      </div>
    </div>
  );
}

function Composer({ community, meta, user, onNeedGuidelines, onCreated }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [category, setCategory] = useState('');
  const [identity, setIdentity] = useState('display');
  const [warning, setWarning] = useState('');
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const fileRef = useRef(null);

  const pickImage = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(f.type)) { toast.error('Use JPG, PNG, or WEBP.'); return; }
    if (f.size > 2_200_000) { toast.error('Image must be under ~2MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!meta.guidelines_accepted) { onNeedGuidelines(); return; }
    if (!text.trim() && !image) { toast.error('Add a message or an image.'); return; }
    if (!category) { toast.error('Pick a category.'); return; }
    setBusy(true);
    try {
      const r = await http.post(`/community/${community}/posts`, { text: text.trim(), category, identity, content_warning: warning.trim() || null, image });
      onCreated(r.data);
      setText(''); setImage(null); setWarning(''); setOpen(false);
      toast.success('Posted.');
    } catch (e) {
      if (e?.response?.status === 428) { onNeedGuidelines(); }
      else toast.error(e?.response?.data?.detail || 'Could not post');
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="community-composer-open"
        className="mt-4 w-full flex items-center gap-3 bg-white rounded-2xl p-4 card-soft text-left hover:ring-1 hover:ring-navy/10 transition-smooth">
        <Avatar author={{ name: user?.name, avatar: user?.avatar_url }}/>
        <span className="text-sm text-navy/45">Share something with the {community === 'supporter' ? 'supporters' : 'brotherhood'}…</span>
      </button>
    );
  }

  return (
    <div className="mt-4 bg-white rounded-2xl p-4 card-soft" data-testid="community-composer">
      {community === 'supporter' && (
        <div className="mb-3 flex items-start gap-2 rounded-xl bg-gold/[0.07] border border-gold/25 px-3 py-2">
          <AlertTriangle size={14} className="text-gold mt-0.5 shrink-0"/>
          <p className="text-[11px] text-navy/70 leading-relaxed">
            Protect your loved one&rsquo;s privacy. Don&rsquo;t share their full name, photo, diagnosis, treatment, workplace, or address without permission.
          </p>
        </div>
      )}
      <textarea data-testid="community-composer-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={5000}
        placeholder="What's on your mind?" autoFocus
        className="w-full bg-[#F9F8F6] border border-navy/10 rounded-xl p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-gold resize-none"/>
      {image && (
        <div className="mt-2 relative inline-block">
          <img src={image} alt="upload preview" className="max-h-40 rounded-xl"/>
          <button onClick={() => setImage(null)} className="absolute -top-2 -right-2 bg-navy text-white rounded-full p-1"><X size={12}/></button>
        </div>
      )}
      <input type="text" value={warning} onChange={(e) => setWarning(e.target.value)} maxLength={120}
        placeholder="Optional content warning (e.g. grief, self-harm)…"
        className="mt-2 w-full bg-[#F9F8F6] border border-navy/10 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-gold"/>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <select data-testid="community-composer-category" value={category} onChange={(e) => setCategory(e.target.value)}
          className="bg-[#F9F8F6] border border-navy/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
          <option value="">Category…</option>
          {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="community-composer-identity" value={identity} onChange={(e) => setIdentity(e.target.value)}
          className="bg-[#F9F8F6] border border-navy/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
          {IDENTITY_OPTS(community).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <p className="mt-2 text-[10px] text-navy/40">Be respectful. Keep it confidential. No hate, threats, or medical diagnosis.</p>
      <div className="mt-3 flex items-center gap-2">
        <input ref={fileRef} type="file" accept="image/*" onChange={pickImage} className="hidden"/>
        <button onClick={() => fileRef.current?.click()} data-testid="community-composer-image" className="p-2 rounded-lg text-navy/60 hover:bg-beige"><ImageIcon size={18}/></button>
        <div className="relative">
          <button onClick={() => setShowEmoji((s) => !s)} data-testid="community-composer-emoji" title="Add an emoji" className="p-2 rounded-lg text-navy/60 hover:bg-beige"><Smile size={18}/></button>
          {showEmoji && <EmojiPicker onPick={(e) => setText((t) => t + e)} onClose={() => setShowEmoji(false)}/>}
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-full text-sm text-navy/60 hover:bg-navy/5">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="community-composer-submit"
            className="px-5 py-2 rounded-full bg-navy text-white text-sm font-medium hover:bg-navy-soft disabled:opacity-40 inline-flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Post
          </button>
        </div>
      </div>
    </div>
  );
}

function PostCard({ post, community, meta, onUpdated, onRemoved, onReport }) {
  const [showComments, setShowComments] = useState(false);
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.text);
  const [revealed, setRevealed] = useState(null);

  const react = async (type) => {
    try {
      const r = await http.post(`/community/${community}/posts/${post.id}/react`, { type });
      onUpdated({ ...post, reaction_counts: r.data.reaction_counts, my_reaction: r.data.my_reaction });
    } catch { toast.error('Could not react'); }
  };
  const toggleSave = async () => {
    try { const r = await http.post(`/community/${community}/posts/${post.id}/save`); onUpdated({ ...post, saved: r.data.saved }); toast.success(r.data.saved ? 'Saved' : 'Removed'); }
    catch { toast.error('Could not save'); }
  };
  const del = async () => {
    if (!window.confirm('Delete this post?')) return;
    try { await http.delete(`/community/${community}/posts/${post.id}`); onRemoved(post.id); }
    catch { toast.error('Delete failed'); }
  };
  const saveEdit = async () => {
    try { const r = await http.patch(`/community/${community}/posts/${post.id}`, { text: editText }); onUpdated(r.data); setEditing(false); }
    catch { toast.error('Edit failed'); }
  };
  const moderate = async (action) => {
    try { await http.post(`/community/admin/posts/${post.id}/moderate`, { action }); onUpdated({ ...post, [action.replace('un', '')]: !action.startsWith('un') && action !== 'restore', removed: action === 'remove' }); toast.success(`Post ${action}`); if (action === 'remove') onRemoved(post.id); }
    catch { toast.error('Action failed'); }
  };
  const reveal = () => {
    setRevealed(post.author?._real_name || post.author?._real_author_id || 'unknown');
  };

  const totalReactions = Object.values(post.reaction_counts || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="bg-white rounded-2xl p-4 card-soft" data-testid={`community-post-${post.id}`}>
      <div className="flex items-start gap-3">
        <Avatar author={post.author}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-navy">{post.author?.name}</span>
            <AuthorBadges badges={post.author?.badges}/>
            {post.pinned && <Pin size={12} className="text-gold"/>}
            {post.locked && <Lock size={12} className="text-navy/40"/>}
            <span className="text-[11px] text-navy/40">· {fmt(post.created_at)}{post.edited ? ' · edited' : ''}</span>
          </div>
          <span className="inline-block mt-1 text-[10px] uppercase tracking-widest bg-beige text-navy/70 rounded-full px-2 py-0.5">{post.category}</span>
        </div>
        <div className="relative">
          <button onClick={() => setMenu(!menu)} data-testid={`community-post-menu-${post.id}`} className="p-1 text-navy/40 hover:text-navy"><MoreHorizontal size={18}/></button>
          {menu && (
            <div className="absolute right-0 mt-1 w-44 bg-white rounded-xl shadow-xl ring-1 ring-navy/10 z-20 py-1 text-sm" onMouseLeave={() => setMenu(false)}>
              {post.can_edit && <button onClick={() => { setEditing(true); setMenu(false); }} className="w-full text-left px-3 py-2 hover:bg-beige flex items-center gap-2"><Pencil size={13}/> Edit</button>}
              {(post.can_edit || post.can_moderate) && <button onClick={() => { del(); setMenu(false); }} className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 flex items-center gap-2"><Trash2 size={13}/> Delete</button>}
              {!post.is_mine && <button onClick={() => { onReport({ type: 'post', id: post.id }); setMenu(false); }} data-testid={`community-report-${post.id}`} className="w-full text-left px-3 py-2 hover:bg-beige flex items-center gap-2"><Flag size={13}/> Report</button>}
              {post.can_moderate && <>
                <div className="border-t border-navy/5 my-1"/>
                <button onClick={() => { moderate(post.pinned ? 'unpin' : 'pin'); setMenu(false); }} className="w-full text-left px-3 py-2 hover:bg-beige flex items-center gap-2"><Pin size={13}/> {post.pinned ? 'Unpin' : 'Pin'}</button>
                <button onClick={() => { moderate(post.locked ? 'unlock' : 'lock'); setMenu(false); }} className="w-full text-left px-3 py-2 hover:bg-beige flex items-center gap-2"><Lock size={13}/> {post.locked ? 'Unlock' : 'Lock'}</button>
                {post.author?.identity === 'anonymous' && <button onClick={() => { reveal(); setMenu(false); }} className="w-full text-left px-3 py-2 hover:bg-beige flex items-center gap-2"><ShieldAlert size={13}/> Reveal author</button>}
              </>}
            </div>
          )}
        </div>
      </div>

      {post.content_warning && (
        <p className="mt-3 text-xs text-gold font-medium">⚠ {post.content_warning}</p>
      )}
      {editing ? (
        <div className="mt-2">
          <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} className="w-full bg-[#F9F8F6] border border-navy/10 rounded-xl p-3 text-sm"/>
          <div className="flex gap-2 mt-2 justify-end">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-sm text-navy/60">Cancel</button>
            <button onClick={saveEdit} className="px-4 py-1.5 rounded-full bg-navy text-white text-sm">Save</button>
          </div>
        </div>
      ) : (
        post.text && <p className="mt-2 text-sm text-navy/90 leading-relaxed whitespace-pre-wrap">{post.text}</p>
      )}
      {post.image && <img src={post.image} alt="post" className="mt-3 rounded-xl max-h-96 w-auto"/>}
      {revealed && <p className="mt-2 text-xs text-red-700">Real author (admin only): {revealed}</p>}

      {/* Reaction bar */}
      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        {meta.reactions.map((rt) => {
          const active = post.my_reaction === rt;
          const count = post.reaction_counts?.[rt] || 0;
          return (
            <button key={rt} onClick={() => react(rt)} data-testid={`community-react-${post.id}-${rt}`}
              className={`text-[11px] px-2.5 py-1 rounded-full transition-smooth ${active ? 'bg-navy text-white' : 'bg-beige text-navy/70 hover:text-navy'}`}>
              {REACTION_LABEL[rt]}{count ? ` ${count}` : ''}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => setShowComments(!showComments)} data-testid={`community-comments-toggle-${post.id}`} className="text-xs text-navy/60 hover:text-navy inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-beige">
            <MessageCircle size={14}/> {post.comment_count || 0}
          </button>
          <button onClick={toggleSave} data-testid={`community-save-${post.id}`} className={`p-1.5 rounded-full hover:bg-beige ${post.saved ? 'text-gold' : 'text-navy/50'}`}>
            <Bookmark size={15} fill={post.saved ? 'currentColor' : 'none'}/>
          </button>
        </div>
      </div>
      {totalReactions > 0 && <p className="mt-1 text-[10px] text-navy/40">{totalReactions} reaction{totalReactions === 1 ? '' : 's'}</p>}

      {showComments && <Comments post={post} community={community} onCount={(n) => onUpdated({ ...post, comment_count: n })} onReport={onReport}/>}
    </div>
  );
}

function Comments({ post, community, onCount, onReport }) {
  const [list, setList] = useState(null);
  const [text, setText] = useState('');
  const [identity, setIdentity] = useState('display');
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [image, setImage] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await http.get(`/community/${community}/posts/${post.id}/comments`); setList(r.data.items); }
    catch { setList([]); }
  }, [community, post.id]);
  useEffect(() => { load(); }, [load]);

  const pickImage = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(f.type)) { toast.error('Use JPG, PNG, or WEBP.'); return; }
    if (f.size > 2_200_000) { toast.error('Image must be under ~2MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(f);
    e.target.value = '';
  };

  const add = async () => {
    if (!text.trim() && !image) return;
    if (post.locked) { toast.error('This discussion is locked.'); return; }
    setBusy(true);
    try {
      const r = await http.post(`/community/${community}/posts/${post.id}/comments`, { text: text.trim(), identity, parent_id: replyTo, image });
      setList((prev) => [...(prev || []), r.data]); setText(''); setReplyTo(null); setImage(null); setShowEmoji(false);
      onCount((post.comment_count || 0) + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not comment'); }
    finally { setBusy(false); }
  };
  const del = async (c) => {
    try { await http.delete(`/community/${community}/comments/${c.id}`); setList((prev) => prev.filter((x) => x.id !== c.id)); onCount(Math.max(0, (post.comment_count || 1) - 1)); }
    catch { toast.error('Delete failed'); }
  };
  const like = async (c) => {
    // optimistic toggle
    setList((prev) => (prev || []).map((x) => x.id === c.id
      ? { ...x, liked_by_me: !x.liked_by_me, like_count: (x.like_count || 0) + (x.liked_by_me ? -1 : 1) }
      : x));
    try {
      const r = await http.post(`/community/${community}/comments/${c.id}/like`);
      setList((prev) => (prev || []).map((x) => x.id === c.id ? { ...x, liked_by_me: r.data.liked, like_count: r.data.like_count } : x));
    } catch { load(); }
  };

  const roots = (list || []).filter((c) => !c.parent_id);
  const repliesOf = (id) => (list || []).filter((c) => c.parent_id === id);

  return (
    <div className="mt-3 border-t border-navy/5 pt-3" data-testid={`community-comments-${post.id}`}>
      {list === null ? <p className="text-xs text-navy/40"><Loader2 size={12} className="inline animate-spin"/></p> : (
        <div className="space-y-3">
          {roots.map((c) => (
            <div key={c.id}>
              <CommentRow c={c} onDelete={del} onReply={() => setReplyTo(c.id)} onReport={onReport} onLike={like} community={community}/>
              <div className="ml-9 mt-2 space-y-2">
                {repliesOf(c.id).map((rc) => <CommentRow key={rc.id} c={rc} onDelete={del} onReport={onReport} onLike={like} community={community}/>)}
              </div>
            </div>
          ))}
        </div>
      )}
      {!post.locked && (
        <div className="mt-3">
          {image && (
            <div className="relative inline-block mb-2">
              <img src={image} alt="reply attachment preview" className="max-h-32 rounded-xl"/>
              <button onClick={() => setImage(null)} className="absolute -top-2 -right-2 bg-navy text-white rounded-full p-1" data-testid={`community-comment-image-remove-${post.id}`}><X size={12}/></button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
              data-testid={`community-comment-input-${post.id}`}
              placeholder={replyTo ? 'Write a reply…' : 'Add a comment…'}
              className="flex-1 bg-[#F9F8F6] border border-navy/10 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"/>
            <input ref={fileRef} type="file" accept="image/*" onChange={pickImage} className="hidden"/>
            <button onClick={() => fileRef.current?.click()} data-testid={`community-comment-image-${post.id}`} title="Add a photo" className="p-2 rounded-full text-navy/55 hover:bg-beige"><ImageIcon size={16}/></button>
            <div className="relative">
              <button onClick={() => setShowEmoji((s) => !s)} data-testid={`community-comment-emoji-${post.id}`} title="Add an emoji" className="p-2 rounded-full text-navy/55 hover:bg-beige"><Smile size={16}/></button>
              {showEmoji && <EmojiPicker onPick={(e) => { setText((t) => t + e); }} onClose={() => setShowEmoji(false)}/>}
            </div>
            <select value={identity} onChange={(e) => setIdentity(e.target.value)} className="bg-[#F9F8F6] border border-navy/10 rounded-full px-2 py-2 text-xs">
              {IDENTITY_OPTS(community).map((o) => <option key={o.value} value={o.value}>{o.label.split(' ')[0]}</option>)}
            </select>
            <button onClick={add} disabled={busy} data-testid={`community-comment-submit-${post.id}`} className="p-2 rounded-full bg-navy text-white disabled:opacity-40"><Send size={14}/></button>
          </div>
        </div>
      )}
      {replyTo && <button onClick={() => setReplyTo(null)} className="mt-1 text-[10px] text-navy/40">Cancel reply</button>}
    </div>
  );
}

function CommentRow({ c, onDelete, onReply, onReport, onLike, community }) {
  void community;
  return (
    <div className="flex items-start gap-2" data-testid={`community-comment-${c.id}`}>
      <Avatar author={c.author}/>
      <div className="flex-1 min-w-0">
        <div className="bg-[#F9F8F6] rounded-2xl px-3 py-2">
          <p className="text-xs font-medium text-navy">{c.author?.name} <span className="text-navy/40 font-normal">· {fmt(c.created_at)}</span></p>
          {c.text && <p className="text-sm text-navy/85 mt-0.5 whitespace-pre-wrap">{c.text}</p>}
          {c.image && <img src={c.image} alt="reply attachment" className="mt-2 rounded-xl max-h-64 w-auto" data-testid={`community-comment-img-${c.id}`}/>}
        </div>
        <div className="flex items-center gap-3 mt-1 ml-1 text-[11px] text-navy/45">
          {onLike && (
            <button onClick={() => onLike(c)} data-testid={`community-comment-like-${c.id}`}
              className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 transition-smooth ${c.liked_by_me ? 'text-gold font-semibold' : 'hover:text-navy'}`}>
              <ThumbsUp size={13} className={c.liked_by_me ? 'fill-gold' : ''}/>{c.like_count > 0 ? c.like_count : ''}
            </button>
          )}
          {onReply && <button onClick={onReply}>Reply</button>}
          {(c.can_edit || c.can_moderate) && <button onClick={() => onDelete(c)} className="hover:text-red-600">Delete</button>}
          {!c.is_mine && <button onClick={() => onReport({ type: 'comment', id: c.id })}>Report</button>}
        </div>
      </div>
    </div>
  );
}

function GuidelinesModal({ community, meta, onClose, onAccepted }) {
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    try { await http.post(`/community/${community}/guidelines/accept`); onAccepted(); }
    catch { toast.error('Could not save'); } finally { setBusy(false); }
  };
  void meta;
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="community-guidelines-modal">
        <h3 className="font-heading text-xl">Community Guidelines</h3>
        <p className="text-sm text-navy/60 mt-1">Before you post, agree to keep this space safe:</p>
        <ul className="mt-3 space-y-1.5 text-sm text-navy/80 list-disc pl-5">
          <li>Respect and confidentiality — what&rsquo;s shared here stays here.</li>
          <li>No harassment, hate speech, threats, or exploitation.</li>
          <li>No medical diagnosis; this isn&rsquo;t a replacement for therapy.</li>
          <li>Don&rsquo;t share others&rsquo; private information without consent.</li>
          <li>No spam, solicitation, or screenshots of private conversations.</li>
        </ul>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-navy/60 hover:bg-navy/5">Not now</button>
          <button onClick={accept} disabled={busy} data-testid="community-guidelines-accept" className="px-5 py-2 rounded-lg bg-navy text-white text-sm font-medium disabled:opacity-40 inline-flex items-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin"/>} I agree
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportModal({ community, target, onClose }) {
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason) { toast.error('Pick a reason.'); return; }
    setBusy(true);
    try {
      await http.post(`/community/${community}/report`, { target_type: target.type, target_id: target.id, reason, detail });
      toast.success('Reported. Our team will review it.'); onClose();
    } catch { toast.error('Could not submit report'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="community-report-modal">
        <h3 className="font-heading text-xl">Report {target.type}</h3>
        <p className="text-sm text-navy/60 mt-1">Help us keep the community safe. Reports are confidential.</p>
        <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto">
          {REPORT_REASONS.map((r) => (
            <label key={r} className="flex items-center gap-2 text-sm cursor-pointer py-1">
              <input type="radio" name="reason" checked={reason === r} onChange={() => setReason(r)} className="accent-navy"/> {r}
            </label>
          ))}
        </div>
        <textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={2} maxLength={1000} placeholder="Optional details…"
          className="mt-2 w-full bg-[#F9F8F6] border border-navy/10 rounded-xl p-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"/>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-navy/60 hover:bg-navy/5">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="community-report-submit" className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-40">Submit report</button>
        </div>
      </div>
    </div>
  );
}
